import type { JSX, ReactNode } from 'react'

import { Flex, Heading, Text } from '@buffer-mono/popcorn'

import styles from './SyncPopoverContent.module.css'

/**
 * Body of a Community sync popover: a title followed by one section per kind of
 * engagement (comments, mentions, likes). Callers own which sections exist and
 * what each one says, so the single-channel and all-channels popovers stay
 * visually identical while describing different things.
 */
export const SyncPopoverContent = ({
  title,
  children,
}: {
  title: string
  children: ReactNode
}): JSX.Element => (
  <Flex direction="column" gap="sm">
    <Heading as="h2" size="sm">
      {title}
    </Heading>
    <Flex direction="column" gap="sm" fullWidth>
      {children}
    </Flex>
  </Flex>
)

/**
 * One labelled row of the popover. `trailing` sits opposite the label for
 * controls that belong to the section header, such as an expand toggle.
 */
export const SyncSection = ({
  icon,
  label,
  trailing,
  children,
}: {
  icon: ReactNode
  label: string
  trailing?: ReactNode
  children: ReactNode
}): JSX.Element => (
  <Flex direction="column" gap="2xs" fullWidth>
    <Flex align="center" justify="between" gap="xs" fullWidth>
      <Flex align="center" gap="xs">
        <span className={styles.sectionIcon} aria-hidden="true">
          {icon}
        </span>
        <Heading as="h3" size="xs">
          {label}
        </Heading>
      </Flex>
      {trailing}
    </Flex>
    {/* Indented to the label's text so descriptions form a single column. */}
    <div className={styles.sectionBody}>{children}</div>
  </Flex>
)

/** Subdued body copy for a section. */
export const SyncSectionText = ({
  children,
}: {
  children: ReactNode
}): JSX.Element => (
  <Text size="sm" color="subtle">
    {children}
  </Text>
)
